import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './assets/styles/broox.scss';
import './assets/styles/tagify.css';
import { ValueCard } from './components/cards/ValueCard';
import { GenderCard } from './components/cards/GenderCard';
import { AgeCard } from './components/cards/AgeCard';
import { EmotionCard } from './components/cards/EmotionCard';
import { ViewsIcon } from './components/icons/ViewsIcon';
import { TrafficIcon } from './components/icons/TrafficIcon';
import { DwellIcon } from './components/icons/DwellIcon';
import { AttentionIcon } from './components/icons/AttentionIcon';
import { InteractionsMetricsCard } from './components/cards/InteractionsMetricsCard';
import { TopFiveCard } from './components/cards/TopFiveCard';
import { TopInteractedCard } from './components/cards/TopInteractedCard';
import { HistoricalCard } from './components/cards/HistoricalCard';
import { Audience, getAudience, getPreviousAudience } from './model/Audience';
import { Occupancy, getOccupancy, getPreviousOccupancy } from './model/Occupancy';
import { Header } from './components/header/Header';
import { Interaction, getInteraction, getPreviousInteraction } from './model/Interaction';
import { Filter, FilterService } from './model/Filter';
import { Collapsed, getAccount, getCampaigns, getCategories, getMedia, getPlaylists } from './model/Collapsed';
import { AccountContext } from './contexts/AccountContext';
import { EntriesByZoneCard } from './components/cards/EntriesByZoneCard';
import { OccupancyCard } from './components/cards/OccupancyCard';
import { PeakTimeCard } from './components/cards/PeakTimeCard';
import { OffPeakTimeCard } from './components/cards/OffPeakTimeCard';
import { MediaPlaybackCard } from './components/cards/MediaPlaybackCard';
import { getMediaPlayback, getMediaPlaybackPage, getPreviousMediaPlayback, MediaPlaybackAggregate as MediaPlayback } from './model/MediaPlayback';
import { AccountParams } from './model/AccountParams';
import { getExcelWorkbook } from './model/Download';
import { LocalDate, LocalTime, RangePreset, ComparePreset } from './model/datePicker/types';
import { createInitialPickerState, resolveB } from './model/datePicker/pickerState';
import { resolveCutoff } from './model/datePicker/resolveCutoff';
import { filterPatchFromApplied } from './components/reactDatePickerV3/filterAdapter';
import { IServiceFactory } from './services/IServiceFactory';
import { ProductSalesCard } from './components/cards/ProductSalesCard';
import { getPreviousProductSales, getProductSales, Sales } from './model/ProductSales';

const App = ({ params, autoRefresh, serviceFactory }: Props) => {
  const getFilterFromParams = (): Filter => {
    const searchParams = new URLSearchParams(window.location.search);
    const locationId = searchParams.getAll('location_id');
    const installationId = searchParams.get('installation_id');
    const campaignId = searchParams.get('campaign_id');
    const zone = searchParams.get('zone');
    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const compareStartDate = searchParams.get('compare_start_date');
    const compareEndDate = searchParams.get('compare_end_date');
    const cutoffParam = searchParams.get('cutoff_time');
    const comparePresetParam = searchParams.get('compare_preset');
    const rangePresetParam = searchParams.get('range_preset');
    const now = DateTime.now();
    const filter = {
      locationId: locationId.length ? locationId : undefined,
      installationId: installationId || undefined,
      campaignId: campaignId || undefined,
      zone: zone || undefined
    };
    // Sin start_date en la URL: primera visita, exactamente el mismo default
    // que el picker (2 semanas contra el periodo anterior), derivado del propio
    // estado inicial del picker para que no puedan divergir.
    if(!startDate) {
      const today = now.toFormat('yyyy-MM-dd') as LocalDate;
      const initial = createInitialPickerState(today);
      return {
        ...filter,
        ...filterPatchFromApplied({
          a: initial.a,
          b: resolveB(initial),
          cutoff: resolveCutoff(initial.a, today, { hour: now.hour, minute: now.minute }),
          rangePreset: initial.rangePreset,
          comparePreset: initial.comparePreset
        })
      };
    }
    return {
      ...filter,
      from: DateTime.fromISO(startDate).toJSDate(),
      to: endDate ? DateTime.fromISO(endDate).toJSDate() : now.endOf('day').toJSDate(),
      compareFrom: compareStartDate ? DateTime.fromISO(compareStartDate).toJSDate() : undefined,
      compareTo: compareEndDate ? DateTime.fromISO(compareEndDate).toJSDate() : undefined,
      // Con start_date pero sin cutoff_time (enlace antiguo, de antes de
      // CX-638 Fase 6): no hay forma de saber si tocaba hoy en su momento,
      // así que no se asume corte.
      cutoff: (cutoffParam as LocalTime) || null,
      // Con start_date pero sin compare_preset (enlace de antes de que esto
      // existiera), queda undefined — el comportamiento que ya tenía.
      comparePreset: (comparePresetParam as ComparePreset) || undefined,
      // Igual: sin range_preset el picker reabre A en 'custom'.
      rangePreset: (rangePresetParam as RangePreset) || undefined
    };
  }

  const [accountContext, setAccountContext] = useState(params);
  const [categories, setCategories] = useState<string[]>([]);
  const [audience, setAudience] = useState<Audience>({ traffic: 0, views: 0, dwell: 0, attention: 0, gender: { datasets: [], labels: [] }, age: { datasets: [], labels: [] }, emotion: { datasets: [], labels: [] } });
  const [previousAudience, setPreviousAudience] = useState<Audience>({ traffic: 0, views: 0, dwell: 0, attention: 0, gender: { datasets: [], labels: [] }, age: { datasets: [], labels: [] }, emotion: { datasets: [], labels: [] } });
  const [interaction, setInteraction] = useState<Interaction>({ count: 0, time: 0, most: { id: '', name: '', count: 0 }, distribution: { labels: [], datasets: [] }});
  const [previousInteraction, setPreviousInteraction] = useState<Interaction>({ count: 0, time: 0, most: { id: '', name: '', count: 0 }, distribution: { labels: [], datasets: [] }});
  const [occupancy, setOccupancy] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entriesByZone: { datasets: [], labels: [] }});
  const [previousOccupancy, setPreviousOccupancy] = useState<Occupancy>({ total: 0, peakTimeHour: '00:00', peakTimeValue: 0, offPeakTimeHour: '00:00', offPeakTimeValue: 0, occupancy: { datasets: [], labels: [] }, entriesByZone: { datasets: [], labels: [] }});
  const [mediaPlayback, setMediaPlayback] = useState<MediaPlayback>({ noViews: 0, partialViews: 0, fullViews: 0, mostViewedCampaign: { name: '', count: 0 }, list: [] });
  const [previousMediaPlayback, setPreviousMediaPlayback] = useState<MediaPlayback>({ noViews: 0, partialViews: 0, fullViews: 0, mostViewedCampaign: { name: '', count: 0 }, list: [] });
  const [productSales, setProductSales] = useState<Sales>({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, productSalesUnits: 0, familySalesAmount: 0, familySalesUnits: 0, brandSalesAmount: 0, brandSalesUnits: 0 });
  const [previousProductSales, setPreviuosProductSales] = useState<Sales>({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, productSalesUnits: 0, familySalesAmount: 0, familySalesUnits: 0, brandSalesAmount: 0, brandSalesUnits: 0 });
  const [filter, setFilter] = useState<Filter>(getFilterFromParams());
  const [displayMediaPlayback, setDisplayMediaPlayback] = useState(false);
  const [displayProductSales, setDisplayProductSales] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const filterRef = useRef(filter);
  const tokenTimeoutRef = useRef<NodeJS.Timeout>();
  const graphicsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const w: any = window;
    w.addEventListener('message', async (event: any) => {
      if(event.data.token) {
        params.token = event.data.token;
        params.token && setParams(filter, params.token);
        clearTimeout(tokenTimeoutRef.current);
        tokenTimeoutRef.current = setTimeout(() => {
          w.parent.postMessage('new-token', '*');
        }, event.data.expiresAt - Date.now() - 300000);
      }
    }, false)
    w.parent.postMessage('token-information', '*');
    load(filter);
    if(autoRefresh) {
      const interval = setInterval(async () => {
        load(filterRef.current);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    filterRef.current = filter;
  }, [ filter ]);

  const onChangeFilter = async (filter: Filter) => {
    const f = { ...filterRef.current, ...filter };
    setFilter(f);
    setParams(f, accountContext.token || '');
    await load(f);
  }

  const onDownloadPdf = async () => {
    setLoading(true);
    if(graphicsContainerRef.current) {
      const canvas = await html2canvas(graphicsContainerRef.current, { scale: 1.25 });
      const image = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      const props = pdf.getImageProperties(image);
      const width = pdf.internal.pageSize.getWidth() - 40;
      const height = (props.height * width) / props.width;
      pdf.setFillColor(248, 247, 250);
      pdf.rect(10, 10, width + 20, height + 20, 'F')
      pdf.setFont('Helvetica');
      pdf.setFontSize(8);
      pdf.setTextColor('#333333');
      // print filter information
      let info = 'Account: ' + params?.name;
      FilterService.getLocationNames(filter) && (info += ` | Location: ${FilterService.getLocationNames(filter)}`);
      filter.installationId && (info += ` | Installation: ${filter.installationName}`);
      filter.campaignId && (info += ` | Campaign: ${filter.campaignName}`);
      info += ` | Period: ${DateTime.fromJSDate(filter.from || new Date()).toLocaleString(DateTime.DATE_SHORT)} - ${DateTime.fromJSDate(filter.to || new Date()).toLocaleString(DateTime.DATE_SHORT)}`;
      const splitInfo = pdf.splitTextToSize(info, width);
      let y = 20;
      for(const i of splitInfo) {
        pdf.text(i, 20, y);
        y += 6;
      }
      pdf.addImage(image, 'PNG', 20, y, width, height);
      pdf.save('analytics.pdf');
    }
    setLoading(false);
  }

  const onDownloadExcel = async () => {
    setLoading(true);
    setDownloadingExcel(true);
    try {
      const workbook = await getExcelWorkbook(params?.name || '', filter, params, mediaPlayback);
      const blob = new Blob([workbook.content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', workbook.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    finally {
      setDownloadingExcel(false);
      setLoading(false);
    }
  }

  const onRefresh = () => {
    console.log('onRefresh');
    load(filterRef.current);
  }

  const load = async (filter: Filter) => {
    setLoading(true);
    try {
      const audience = await getAudience(filter, params);
      setAudience(audience);
      const categories = await getCategories(filter, params);
      setCategories(categories);
      const interaction = await getInteraction(filter, params);
      setInteraction(interaction);
      // const occupancy = await getOccupancy(filter, params);
      // setOccupancy(occupancy);
      const previousAudience = await getPreviousAudience(filter, params);
      setPreviousAudience(previousAudience);
      const previousInteraction = await getPreviousInteraction(filter, params);
      setPreviousInteraction(previousInteraction);
      // const previousOccupancy = await getPreviousOccupancy(filter, params);
      // setPreviousOccupancy(previousOccupancy);
      // const media = await getMedia(filter, params);
      // const campaigns = await getCampaigns(filter, params);
      // const playlists = await getPlaylists(filter, params);
      // const mediaPlayback = await getMediaPlayback(filter, campaigns, playlists, media, params);
      // console.log('mediaPlayback', mediaPlayback);
      const mediaPlayback = await getMediaPlaybackPage(filter, 0, 1, '', params);
      setDisplayMediaPlayback(mediaPlayback.list.length > 0);
      // setMediaPlayback(mediaPlayback);
      // if(mediaPlayback.list.length > 0) {
      //   const previousMediaPlayback = await getPreviousMediaPlayback(filter, campaigns, playlists, media, params);
      //   setPreviousMediaPlayback(previousMediaPlayback);
      // }
      if(accountContext.modules && accountContext.modules.includes('sales')) {
        const productSales = await getProductSales(filter, serviceFactory.getSaleService(), params);
        setDisplayProductSales(productSales.productSales.length > 0 || productSales.familySales.length > 0 || productSales.brandSales.length > 0);
        setProductSales(productSales);
        const previousProductSales = await getPreviousProductSales(filter, serviceFactory.getSaleService(), params);
        setPreviuosProductSales(previousProductSales)
      }
      else {
       setProductSales({ productSales: [], familySales: [], brandSales: [], productSalesAmount: 0, productSalesUnits: 0, familySalesAmount: 0, familySalesUnits: 0, brandSalesAmount: 0, brandSalesUnits: 0 });
       setDisplayProductSales(false);
      }
    }
    catch(error) {
      console.log(error);
    }
    finally {
      setLoading(false);
    }
  }

  const setParams = (filter: Filter, token: string) => {
    const params = new URLSearchParams();
    filter.locationId?.forEach(locationId => params.append('location_id', locationId));
    filter.installationId && params.set('installation_id', filter.installationId);
    filter.campaignId && params.set('campaign_id', filter.campaignId);
    filter.zone && params.set('zone', filter.zone);
    filter.from && params.set('start_date', DateTime.fromJSDate(filter.from).toISO() || '');
    filter.to && params.set('end_date', DateTime.fromJSDate(filter.to).toISO() || '');
    filter.compareFrom && params.set('compare_start_date', DateTime.fromJSDate(filter.compareFrom).toISO() || '');
    filter.compareTo && params.set('compare_end_date', DateTime.fromJSDate(filter.compareTo).toISO() || '');
    filter.cutoff && params.set('cutoff_time', filter.cutoff);
    filter.comparePreset && params.set('compare_preset', filter.comparePreset);
    filter.rangePreset && params.set('range_preset', filter.rangePreset);
    let url = '';
    const path = window.location.pathname.split('/');
    path.shift();
    if(path.length === 2 && token) {
      path[1] = token;
      url = `${window.location.origin}/${path.join('/')}`;
    }
    history.pushState(null, 'filter', `${url}?${params.toString()}`);
  }

  const displayInteractions = categories.includes('sensor') || categories.includes('onevent');
  return (
    <AccountContext.Provider value={accountContext}>
      <div className="container-xxl">
        <Header filter={filter} loading={loading} downloadingExcel={downloadingExcel} onChange={onChangeFilter} onDownloadPdf={onDownloadPdf} onDownloadExcel={onDownloadExcel} onRefresh={autoRefresh ? undefined : onRefresh} />
        <div ref={graphicsContainerRef} style={{ backgroundColor: '#f8f7fa' }}>
          {/* <div className="row">
            <div className="col-6 col-lg-3 mb-4">
              <OccupancyCard total={occupancy.total} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <EntriesByZoneCard input={occupancy.entriesByZone} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <PeakTimeCard hour={occupancy.peakTimeHour} total={occupancy.peakTimeValue} previousTotal={previousOccupancy.peakTimeValue} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
            <OffPeakTimeCard hour={occupancy.offPeakTimeHour} total={occupancy.offPeakTimeValue} previousTotal={previousOccupancy.offPeakTimeValue} />
            </div>
          </div> */}
          <div className="row">
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title="Traffic" value={audience.traffic} previousValue={previousAudience.traffic} color="success" icon={<TrafficIcon />} comparePreset={filter.comparePreset} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title="Watchers" value={audience.views} previousValue={previousAudience.views} color="primary-purple" icon={<ViewsIcon />} comparePreset={filter.comparePreset} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title="Dwell time" value={audience.dwell} previousValue={previousAudience.dwell} format={(value: number) => value.toFixed(1) + 's'} color="warning" icon={<DwellIcon />} comparePreset={filter.comparePreset} />
            </div>
            <div className="col-6 col-lg-3 mb-4">
              <ValueCard title="Avg. attention time" value={audience.attention} previousValue={previousAudience.attention} format={(value: number) => value.toFixed(1) + 's'} color="info" icon={<AttentionIcon />} comparePreset={filter.comparePreset} />
            </div>
          </div>
          <div className="row d-flex flex-wrap">
            <div className="col-4 mb-4">
              <GenderCard input={audience.gender} />
            </div>
            <div className="col-4 mb-4">
              <AgeCard input={audience.age} />
            </div>
            <div className="col-4 mb-4">
              <EmotionCard input={audience.emotion} />
            </div>
          </div>
          {
            displayMediaPlayback &&
              <div className="row">
                <div className="col-12 mb-4">
                  <MediaPlaybackCard filter={filter} />
                </div>
              </div>
          }
          {
            displayProductSales &&
              <div className="row">
                <div className="col-12 mb-4">
                  <ProductSalesCard sales={productSales} previousSales={previousProductSales} comparePreset={filter.comparePreset} />
                </div>
              </div>
          }
          <div className="row">
            {
              displayInteractions ?
              <>
                <div className="col-12 col-lg-4 mb-4">
                  <div className="row h-100">
                    <div className="col-6 col-lg-12 mb-lg-4">
                      <InteractionsMetricsCard count={interaction.count} previousCount={previousInteraction.count} time={interaction.time} previousTime={previousInteraction.time} />
                    </div>
                    <div className="col-6 col-lg-12">
                      <TopInteractedCard name={interaction.most.name} count={interaction.most.count} previousCount={previousInteraction.most.count} total={interaction.count} comparePreset={filter.comparePreset} />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4 mb-4">
                  <TopFiveCard input={interaction.distribution} previousInput={previousInteraction.distribution} />
                </div>
              </> : ''
            }
            <div className={`col-12 ${displayInteractions ? 'col-lg-4' : ''} mb-4`}>
              <HistoricalCard filter={filter} shared={displayInteractions} saleService={serviceFactory.getSaleService()} />
            </div>
          </div>
        </div>
      </div>
    </AccountContext.Provider>
  );
}

interface Props {
  params: AccountParams,
  autoRefresh: boolean,
  serviceFactory: IServiceFactory
}

export { App };
